import React, { useState } from 'react';
import { VenueSearchFilters, VenueType } from '../../../types/venue';
import { theme } from '../../../theme';

interface VenueFilterSidebarProps {
  filters: VenueSearchFilters;
  onFiltersChange: (filters: Partial<VenueSearchFilters>) => void;
  onReset: () => void;
}

const VenueFilterSidebar: React.FC<VenueFilterSidebarProps> = ({
  filters,
  onFiltersChange,
  onReset,
}) => {
  const [showMoreAmenities, setShowMoreAmenities] = useState(false);

  const handleVenueTypeChange = (type: VenueType | undefined) => {
    onFiltersChange({ venueType: type });
  };

  const handlePriceChange = (field: 'minPrice' | 'maxPrice', value: string) => {
    const numValue = value === '' ? undefined : parseFloat(value);
    onFiltersChange({ [field]: numValue });
  };

  const handleAmenityToggle = (amenity: keyof VenueSearchFilters) => {
    const currentValue = filters[amenity];
    onFiltersChange({ [amenity]: currentValue ? undefined : true });
  };

  const containerStyle: React.CSSProperties = {
    width: '280px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '24px',
    boxShadow: theme.colors.shadow,
    maxHeight: 'calc(100vh - 100px)',
    overflowY: 'auto',
    position: 'sticky',
    top: '24px',
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '24px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '12px',
    fontFamily: theme.fonts.heading,
  };

  const buttonGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const getButtonStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '12px 16px',
    backgroundColor: isActive ? theme.colors.primary : theme.colors.background,
    color: isActive ? '#000' : theme.colors.text,
    border: `2px solid ${isActive ? theme.colors.primary : theme.colors.border}`,
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: isActive ? theme.fonts.weightBold : theme.fonts.weightNormal,
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left',
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.input,
    fontSize: theme.fonts.sizeBody,
    marginBottom: '8px',
  };

  const checkboxContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0',
    cursor: 'pointer',
  };

  const checkboxStyle: React.CSSProperties = {
    width: '20px',
    height: '20px',
    marginRight: '12px',
    cursor: 'pointer',
    accentColor: theme.colors.primary,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.text,
    cursor: 'pointer',
  };

  const resetButtonStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    backgroundColor: theme.colors.danger,
    color: theme.colors.text,
    border: 'none',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    marginTop: '24px',
  };

  return (
    <div style={containerStyle}>
      {/* Venue Type Filter */}
      <div style={sectionStyle}>
        <h3 style={titleStyle}>Tipo de Cancha</h3>
        <div style={buttonGroupStyle}>
          <button
            style={getButtonStyle(!filters.venueType)}
            onClick={() => handleVenueTypeChange(undefined)}
          >
            Todas
          </button>
          <button
            style={getButtonStyle(filters.venueType === 'FUTBOL_5')}
            onClick={() => handleVenueTypeChange('FUTBOL_5')}
          >
            Fútbol 5
          </button>
          <button
            style={getButtonStyle(filters.venueType === 'FUTBOL_7')}
            onClick={() => handleVenueTypeChange('FUTBOL_7')}
          >
            Fútbol 7
          </button>
          <button
            style={getButtonStyle(filters.venueType === 'FUTBOL_11')}
            onClick={() => handleVenueTypeChange('FUTBOL_11')}
          >
            Fútbol 11
          </button>
        </div>
      </div>

      {/* Price Range Filter */}
      <div style={sectionStyle}>
        <h3 style={titleStyle}>Rango de Precio</h3>
        <input
          type="number"
          placeholder="Precio mínimo"
          value={filters.minPrice || ''}
          onChange={(e) => handlePriceChange('minPrice', e.target.value)}
          style={inputStyle}
        />
        <input
          type="number"
          placeholder="Precio máximo"
          value={filters.maxPrice || ''}
          onChange={(e) => handlePriceChange('maxPrice', e.target.value)}
          style={inputStyle}
        />
      </div>

      {/* Amenities Filter */}
      <div style={sectionStyle}>
        <h3 style={titleStyle}>Comodidades</h3>

        <div
          style={checkboxContainerStyle}
          onClick={() => handleAmenityToggle('hasParking')}
        >
          <input
            type="checkbox"
            checked={!!filters.hasParking}
            onChange={() => {}}
            style={checkboxStyle}
          />
          <label style={labelStyle}>Estacionamiento</label>
        </div>

        <div
          style={checkboxContainerStyle}
          onClick={() => handleAmenityToggle('isIndoor')}
        >
          <input
            type="checkbox"
            checked={!!filters.isIndoor}
            onChange={() => {}}
            style={checkboxStyle}
          />
          <label style={labelStyle}>Techado</label>
        </div>

        <div
          style={checkboxContainerStyle}
          onClick={() => handleAmenityToggle('hasLighting')}
        >
          <input
            type="checkbox"
            checked={!!filters.hasLighting}
            onChange={() => {}}
            style={checkboxStyle}
          />
          <label style={labelStyle}>Iluminación</label>
        </div>

        {showMoreAmenities && (
          <>
            <div
              style={checkboxContainerStyle}
              onClick={() => handleAmenityToggle('hasBathrooms')}
            >
              <input
                type="checkbox"
                checked={!!filters.hasBathrooms}
                onChange={() => {}}
                style={checkboxStyle}
              />
              <label style={labelStyle}>Baños</label>
            </div>

            <div
              style={checkboxContainerStyle}
              onClick={() => handleAmenityToggle('hasChangingRooms')}
            >
              <input
                type="checkbox"
                checked={!!filters.hasChangingRooms}
                onChange={() => {}}
                style={checkboxStyle}
              />
              <label style={labelStyle}>Vestuarios</label>
            </div>
          </>
        )}

        <button
          onClick={() => setShowMoreAmenities(!showMoreAmenities)}
          style={{
            ...labelStyle,
            marginTop: '8px',
            background: 'none',
            border: 'none',
            color: theme.colors.primary,
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          {showMoreAmenities ? 'Ver menos' : 'Ver más'}
        </button>
      </div>

      {/* Reset Button */}
      <button style={resetButtonStyle} onClick={onReset}>
        Limpiar Filtros
      </button>
    </div>
  );
};

export default VenueFilterSidebar;
